import React, { useState, useEffect, useLayoutEffect } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import Services, { SERVICES } from './components/Services';
import Booking from './components/Booking';
import MembershipMembers from './components/MembershipMembers';
import Newsletter from './components/Newsletter';
import Membership from './components/Membership';
import Footer from './components/Footer';
import ChartCalculator from './components/ChartCalculator';
import CelestialBackground from './components/CelestialBackground';
import ServiceDetail from './components/ServiceDetail';
import SplashPage from './components/SplashPage';
import AdminDashboard from './components/AdminDashboard';
import MemberPortal from './components/MemberPortal';
import BlogSection from './components/BlogSection';
import LiveTeaching from './components/LiveTeaching';
import OnboardingTutorial from './components/OnboardingTutorial';
import { PrivacyPolicy, TermsOfService } from './components/LegalPages';
import { PhoneCall, X, Mail, ShieldCheck, ChevronUp, AlertCircle, RefreshCw, Gem, Sparkles, MapPin, Clock } from 'lucide-react';
import { MemberRecord, Language } from './types';
import { translations } from './translations';

const AboutAllie: React.FC<{ onBook: () => void, lang: Language }> = ({ onBook, lang }) => {
  const t = (translations[lang] || translations.en).about;
  return (
    <section id="about" className="py-24 bg-slate-900/50 relative overflow-hidden backdrop-blur-sm border-y border-indigo-900/20">
      <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center gap-16 relative z-10">
        <div className="flex-1 relative">
          <div className="w-full aspect-[4/5] bg-indigo-950 relative overflow-hidden rounded-sm border-2 border-amber-500/20 shadow-2xl">
            <img 
              src="https://lh3.googleusercontent.com/d/1T2UOY_Khvwp7vEwVDkhSwE_v78HyhHYL" 
              alt="Allie Pak" 
              className="w-full h-full object-cover mix-blend-luminosity hover:mix-blend-normal transition-all duration-700"
            />
          </div>
          <div className="absolute -bottom-6 -right-6 w-32 h-32 border-b-2 border-r-2 border-amber-400/50" />
          <div className="absolute -top-6 -left-6 w-32 h-32 border-t-2 border-l-2 border-amber-400/50" />
        </div>
        <div className="flex-1 text-left">
          <h2 className="text-amber-400 text-sm tracking-[0.2em] uppercase mb-4">{t.tag}</h2>
          <h3 id="about-title" className="text-4xl md:text-5xl font-serif text-slate-50 mb-8">{t.title}</h3>
          
          <div className="flex flex-wrap gap-4 mb-8">
            <div className="flex items-center gap-2 px-4 py-2 bg-indigo-950/60 border border-indigo-500/30 rounded-full text-indigo-100 text-xs">
              <MapPin size={14} className="text-amber-500" /> Los Angeles, CA
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-indigo-950/60 border border-indigo-500/30 rounded-full text-indigo-100 text-xs">
              <Clock size={14} className="text-amber-500" /> Available 24/7
            </div>
          </div>

          <div className="space-y-6 text-indigo-200/80 leading-relaxed font-light">
            <p>
              With over a decade of studying ancient Hellenistic and modern psychological astrology, I approach your chart as a sacred map. Now based in Los Angeles, I provide consultations across all global time zones.
            </p>
            <p>
              My mission is to provide clarity in times of transition and to validate the intuitive nudges you've been feeling. Astrology isn't just about prediction; it's about empowerment.
            </p>
            <div className="p-5 bg-indigo-950/40 border-l-2 border-amber-500 rounded-r-sm space-y-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-amber-100 font-serif text-left">
                    <PhoneCall size={18} className="text-amber-400" />
                    <span>{t.connect}</span>
                </div>
                <p className="text-sm text-left">
                  All readings are personal conversations conducted via **phone**, **video**, or **direct message**. We allow the planetary energies to translate clearly without distractions.
                </p>
              </div>
              
              <div className="space-y-3 pt-3 border-t border-indigo-900/50">
                <div className="flex items-center gap-3 text-amber-100 font-serif text-left">
                    <ShieldCheck size={18} className="text-emerald-500" />
                    <span>{t.secure}</span>
                </div>
                <p className="text-sm text-left">
                  To ensure our time is fully dedicated to the heavens, payment is collected via **PayPal** at the time of booking. This secures your alignment in my calendar instantly.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-10 flex flex-wrap gap-8 items-center">
            <div className="flex gap-12">
              <div className="text-left">
                <p className="text-3xl font-serif text-amber-400">10+</p>
                <p className="text-[10px] uppercase tracking-widest text-indigo-400">{t.exp}</p>
              </div>
              <div className="text-left">
                <p className="text-3xl font-serif text-amber-400">5k+</p>
                <p className="text-[10px] uppercase tracking-widest text-indigo-400">{t.delivered}</p>
              </div>
            </div>
            <button 
              onClick={onBook}
              className="px-8 py-4 bg-amber-500 text-slate-950 font-bold uppercase tracking-widest text-xs hover:bg-amber-400 transition-all rounded-sm shadow-xl"
            >
              {t.btn}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const App: React.FC = () => {
  const [hasEntered, setHasEntered] = useState(() => {
    return localStorage.getItem('allie_has_entered') === 'true';
  });

  const [showTutorial, setShowTutorial] = useState(false);
  const [currentLang, setCurrentLang] = useState<Language>('en');
  const [appError, setAppError] = useState<string | null>(null);
  
  const [currentView, setCurrentView] = useState<{ type: 'home' | 'service' | 'admin' | 'member' | 'privacy' | 'tos'; id?: string }>(() => {
    const saved = localStorage.getItem('allie_current_view');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed;
      } catch (e) {
        return { type: 'home' };
      }
    }
    return { type: 'home' };
  });

  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isNewsletterOpen, setIsNewsletterOpen] = useState(false);
  const [preSelectedServices, setPreSelectedServices] = useState<string[]>([]);
  const [loggedInMember, setLoggedInMember] = useState<MemberRecord | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    localStorage.setItem('allie_has_entered', hasEntered.toString());
  }, [hasEntered]);

  useEffect(() => {
    localStorage.setItem('allie_current_view', JSON.stringify(currentView));
  }, [currentView]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 200);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    const handleError = (e: ErrorEvent) => {
      console.error("Celestial alignment interrupted:", e.error);
      if (e.message.includes("API_KEY") || e.message.includes("GoogleGenAI")) {
        setAppError("Celestial keys missing. Please refresh or check environment variables.");
      }
    };
    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('error', handleError);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (isBookingOpen || isNewsletterOpen || !hasEntered || showTutorial) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isBookingOpen, isNewsletterOpen, hasEntered, showTutorial]);

  const handleEnter = () => {
    setHasEntered(true);
    const skipTutorial = localStorage.getItem('allie_skip_tutorial') === 'true';
    if (!skipTutorial) {
      setShowTutorial(true);
    }
  };

  if (appError) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-8 text-center">
        <AlertCircle size={48} className="text-amber-500 mb-6" />
        <h1 className="text-2xl font-serif text-white uppercase tracking-widest mb-4">Cosmic Alignment Interrupted</h1>
        <p className="text-indigo-400 max-w-md mb-8">{appError}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="px-8 py-4 bg-indigo-600 text-white rounded-sm flex items-center gap-2 uppercase tracking-widest text-[10px] font-bold"
        >
          <RefreshCw size={14} /> Retry Alignment
        </button>
      </div>
    );
  }

  const navigateToService = (id: string) => {
    setCurrentView({ type: 'service', id });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBookClick = (serviceId?: string) => {
    if (serviceId) {
      setPreSelectedServices([serviceId]);
    } else {
      setPreSelectedServices([]);
    }
    setIsBookingOpen(true);
  };

  const handleMemberPortalClick = () => {
    setHasEntered(true);
    setCurrentView({ type: 'member' });
  };

  const handleLegalNavigation = (type: 'privacy' | 'tos') => {
    setCurrentView({ type });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const selectedService = currentView.id ? SERVICES.find(s => s.id === currentView.id) : null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 relative overflow-hidden flex flex-col">
      {!hasEntered && (
        <SplashPage 
          onEnter={handleEnter} 
          onSignIn={handleMemberPortalClick}
        />
      )}

      {hasEntered && showTutorial && (
        <OnboardingTutorial 
          lang={currentLang} 
          onComplete={() => setShowTutorial(false)} 
        />
      )}
      
      {hasEntered && !['admin', 'member', 'privacy', 'tos'].includes(currentView.type) && (
        <Header 
          onHomeClick={() => {
            setCurrentView({ type: 'home' });
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }} 
          isSubPage={currentView.type !== 'home'} 
          onBookClick={() => handleBookClick()}
          onNewsletterClick={() => setIsNewsletterOpen(true)}
          onMemberPortalClick={handleMemberPortalClick}
          currentLang={currentLang}
          onLangChange={setCurrentLang}
        />
      )}

      <div className={`transition-all duration-1000 ease-out-expo ${hasEntered ? 'opacity-100 scale-100 flex-1 flex flex-col' : 'opacity-0 scale-95 pointer-events-none'}`}>
        
        {currentView.type === 'admin' ? (
          <AdminDashboard onExit={() => setCurrentView({ type: 'home' })} />
        ) : currentView.type === 'member' ? (
          <MemberPortal 
            onExit={() => setCurrentView({ type: 'home' })} 
            onLogin={(member) => setLoggedInMember(member)} 
            onUpdateMember={(member) => setLoggedInMember(member)}
            onBookReading={() => handleBookClick()}
            initialMember={loggedInMember} 
            lang={currentLang}
          />
        ) : currentView.type === 'privacy' ? (
          <PrivacyPolicy onBack={() => setCurrentView({ type: 'home' })} />
        ) : currentView.type === 'tos' ? (
          <TermsOfService onBack={() => setCurrentView({ type: 'home' })} />
        ) : (
          <>
            <CelestialBackground />
            
            <main className="relative z-10 pt-16 flex-1 pb-32 lg:pb-0">
              {currentView.type === 'home' ? (
                <>
                  <Hero 
                    lang={currentLang}
                    onBookClick={() => handleBookClick()} 
                    onChartClick={() => document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' })} 
                  />
                  <Services lang={currentLang} onSelectService={navigateToService} />
                  
                  <LiveTeaching onBook={() => handleBookClick('teaching')} />
                  <BlogSection />
                  <ChartCalculator />
                  <Membership onJoinSuccess={(m) => {
                    setLoggedInMember(m);
                    setCurrentView({ type: 'member' });
                  }} />
                  <MembershipMembers />
                  <AboutAllie onBook={() => handleBookClick()} lang={currentLang} />
                </>
              ) : selectedService ? (
                <ServiceDetail 
                  service={selectedService} 
                  onBack={() => setCurrentView({ type: 'home' })}
                  onBook={(id) => handleBookClick(id)}
                />
              ) : null}
            </main>

            <Footer 
              onAdminClick={() => setCurrentView({ type: 'admin' })} 
              onLegalClick={handleLegalNavigation}
            />
          </>
        )}

        <div className="fixed bottom-32 right-8 z-[200] flex flex-col gap-4 items-center lg:bottom-8">
          {showScrollTop && !['admin', 'member', 'privacy', 'tos'].includes(currentView.type) && (
            <button 
              id="scroll-top-btn"
              onClick={scrollToTop}
              className="p-4 bg-amber-500 text-slate-950 rounded-full shadow-[0_0_25px_rgba(245,158,11,0.5)] hover:scale-110 active:scale-95 transition-all group animate-fade-in"
              title="Return to the Heavens"
            >
              <ChevronUp size={24} className="group-hover:-translate-y-1 transition-transform" />
            </button>
          )}
        </div>

        {isBookingOpen && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-xl animate-fade-in">
            <button 
              onClick={() => setIsBookingOpen(false)}
              className="absolute top-8 right-8 z-[10010] p-3 bg-slate-900 border border-amber-500/20 text-amber-500 hover:text-white rounded-full transition-all shadow-2xl"
            >
              <X size={24} />
            </button>
            <div className="w-full max-w-5xl max-h-[85vh] overflow-y-auto bg-slate-900 border border-amber-500/20 rounded-sm shadow-2xl relative custom-scrollbar">
              <div className="p-4 md:p-8">
                <Booking initialServiceIds={preSelectedServices} memberProfile={loggedInMember} />
              </div>
            </div>
          </div>
        )}

        {isNewsletterOpen && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-xl animate-fade-in">
            <button 
              onClick={() => setIsNewsletterOpen(false)}
              className="absolute top-8 right-8 z-[10010] p-3 bg-slate-900 border border-amber-500/20 text-amber-500 hover:text-white rounded-full transition-all shadow-2xl"
            >
              <X size={24} />
            </button>
            <div className="w-full max-w-2xl bg-slate-900 border border-amber-500/20 rounded-sm shadow-2xl relative">
              <Newsletter />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;